import { del, post } from '@api/api';
import { httpPutItem, useUpdateSingleItem } from '@api/generics';
import QueryKeys from '@api/index';
import API_ROUTES from '@api/routes';
import { useQueryClient } from '@tanstack/react-query';
import { AnalyticsActionName } from '@utils/analytics';
import { useToast } from 'hooks';
import useAnalytics from 'hooks/useAnalytics';
import { AuthContext } from 'hooks/useAuth';
import React from 'react';
import { IConsoleAuth } from 'types/login';
import { ToastSeverity } from 'types/toast';
import { IWizard } from 'types/wizard';

export const useWizardUpdate = (state: IWizard) => {
	const queryClient = useQueryClient();
	const { consoleUser } = React.useContext<IConsoleAuth>(AuthContext);
	const { addToast } = useToast();
	const track = useAnalytics();
	const updateMutation = useUpdateSingleItem<IWizard>(
		QueryKeys.wizard,
		API_ROUTES.v1.wizard.update,
		httpPutItem
	);

	const update = (wizardUpdate: Partial<IWizard>) => {
		try {
			const newWizard = {
				...state,
				...wizardUpdate
			};
			track(AnalyticsActionName.wizardStep, {
				...newWizard,
				descopeUserId: consoleUser?.descopeUserId || ''
			});
			return updateMutation.mutateAsync(newWizard);
		} catch (error) {
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('wizard.error'),
				error
			});
		}
		return Promise.reject();
	};

	const apply = async () => {
		try {
			await post(API_ROUTES.v1.wizard.apply);
		} catch (error) {
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('wizard.generating.error'),
				error
			});
			return false;
		}
		return true;
	};

	const startOver = async (id: string) => {
		try {
			await del(API_ROUTES.v1.wizard.startOver(id));
			await queryClient.refetchQueries({
				queryKey: [QueryKeys.wizard]
			});
		} catch (error) {
			addToast({
				severity: ToastSeverity.Error,
				content: intl.tr('wizard.startOver.error'),
				error
			});
			return false;
		}
		return true;
	};

	return {
		update,
		apply,
		startOver
	};
};
